<template>
    <div>
        <div class="answers-required-box flex items-center" v-if="quizType != 'poll' && answers && answers.length">
            <span id="label-answers-required" class="shadow-text-none">{{
                $t('question_header_answers_required')
            }}</span>
            <XCheckbox
                @input="toggleAnswersRequired('one')"
                :id="`answer-required-one-${questionIndex}`"
                :name="`answer-required-${questionIndex}`"
                :checked="!answerRequired || answerRequired == 'one'"
                :disabled="!canEditQuiz"
                size="md"
                theme="dark"
            >
                <span class="shadow-text-none">{{ $t('question_one') }}</span>
            </XCheckbox>
            <XCheckbox
                @input="toggleAnswersRequired('multiple')"
                :id="`answer-required-multiple-${questionIndex}`"
                :name="`answer-required-${questionIndex}`"
                :checked="answerRequired == 'multiple'"
                :disabled="answers.filter(e => e.is_correct).length <= 1 || !canEditQuiz"
                size="md"
                theme="dark"
            >
                <span class="shadow-text-none">{{ $t('question_multiple') }}</span>
            </XCheckbox>
        </div>
        <div class="answers-section" v-if="questionType != 'hotspot'">
            <div v-for="(answer, index) in answers" :key="index">
                <QuizCreatorImage
                    @remove="removeAnswerImage(index)"
                    :ref="`answer-image-${index}`"
                    :id="`answer-image-${questionIndex}-${index}`"
                    :class="{ 'pointer-events-none': !canEditQuiz }"
                    :question-index="questionIndex"
                    :answer-index="index"
                    :image="answer.image"
                    :image-original="answer.image_original"
                    :image-source="answer.image_source"
                    :image-coordinates="answer.image_coordinates"
                    color="#ccc"
                    border-color="#ccc"
                    border-style="dashed"
                    location="answer"
                />
                <div class="relative mt-4">
                    <XCheckbox
                        @input="checkedAnswer(index)"
                        :show-content="false"
                        :checked="answer.is_correct"
                        :disabled="!canEditQuiz"
                        class="absolute answer-check"
                        type="checkbox"
                        size="lg"
                        theme="dark"
                    ></XCheckbox>

                    <XInput
                        v-validate="{ required: !answer.image }"
                        @input="inputAnswer($event, index)"
                        :value="answer.title"
                        :placeholder="$t('question_placeholder_choice')"
                        :name="`answer-${questionIndex}-${index}`"
                        :disabled="!canEditQuiz"
                        data-vv-as="choice"
                        autocomplete="off"
                        maxlength="200"
                    />

                    <div class="input-option-btn flex absolute">
                        <span
                            @click="openUploadMedia(questionIndex, index)"
                            :class="canEditQuiz ? 'cursor-pointer' : 'cursor-not-allowed'"
                        >
                            <i class="far fa-image image-icon shadow-text-none"></i>
                        </span>
                        <span
                            v-if="answers.length > 1"
                            @click="handleRemoveAnswer(index)"
                            :class="canEditQuiz ? 'cursor-pointer' : 'cursor-not-allowed'"
                        >
                            <i class="far fa-trash-alt image-icon shadow-text-none pointer-events-none"></i>
                        </span>
                    </div>
                </div>
                <span
                    v-if="errors.has(`answer-${questionIndex}-${index}`) && !answer.title"
                    class="has-error text-red text-center mt-2 font-size-14 shadow-text-none pointer-events-none"
                >
                    * {{ errors.first(`answer-${questionIndex}-${index}`) }}
                </span>
            </div>
            <XButton
                class="w-full cursor-pointer shadow-text-none mt-4"
                bg-color="#f7f8fa"
                color="#000"
                font-weight="400"
                :disabled="!canEditQuiz"
                @click="addAnswer()"
            >
                <span>{{ $t('question_modal_button_add_choice') }}</span>
            </XButton>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex'
import quizCreator from '@/mixins/quiz-creator.js'
import popup from '@/lib/popup.js'
import XCheckbox from '@/components/v2/XCheckbox'
import XInput from '@/components/v2/XInput'
import XButton from '@/components/v2/XButton'
import QuizCreatorImage from '@/components/quiz-creator/QuizCreatorImage'

export default {
    components: {
        XCheckbox,
        XInput,
        XButton,
        QuizCreatorImage
    },

    mixins: [quizCreator],

    props: {
        questionIndex: {
            type: Number,
            required: true
        },

        questionType: {
            type: String,
            default: 'basic'
        },

        answerRequired: {
            type: String,
            default: 'one'
        },

        answers: {
            type: Array,
            required: true
        }
    },

    computed: {
        ...mapState({
            quizType: state => state.question.type
        })
    },

    mounted() {
        this.validateCorrectAnswer()
    },

    methods: {
        checkedAnswer(answerIndex) {
            if (!this.canEditQuiz) return

            this.$store.commit('toggleChooseAnswer', { questionIndex: this.questionIndex, answerIndex })

            const correctCount = this.answers.filter(e => e.is_correct).length

            this.$nextTick(() => {
                if (correctCount < 1) {
                    popup.alert(this.$t('question_alert_select_one_choice'))
                    this.$store.commit('toggleChooseAnswer', { questionIndex: this.questionIndex, answerIndex })
                }

                if (correctCount === 1) {
                    this.toggleAnswersRequired('one')
                }
            })
        },

        toggleAnswersRequired(value) {
            if (!this.canEditQuiz) return

            if (value != 'one' && value != 'multiple') return

            if (value == 'multiple') {
                value = this.answers.filter(e => e.is_correct).length > 1 ? 'multiple' : 'one'

                if (value == 'one') {
                    document.querySelector(`#answer-required-one-${this.questionIndex}`).checked = true
                    document.querySelector(`#answer-required-multiple-${this.questionIndex}`).checked = false
                }
            }

            this.$store.commit('toggleAnswersRequired', { value, questionIndex: this.questionIndex })
        },

        handleRemoveAnswer(index) {
            this.removeAnswer(index)
            this.validateCorrectAnswer()
        },

        validateCorrectAnswer() {
            if (this.answers.length && !this.answers.filter(answer => answer.is_correct).length) {
                this.checkedAnswer(0)
            }

            const correctCount = this.answers.filter(e => e.is_correct).length

            if (correctCount <= 1) {
                this.toggleAnswersRequired('one')
            }
        }
    }
}
</script>

<style>
</style>
